// /admin/analytics — 6-KPI dashboard ported from Cambonic_SaaS
// admin/AnalyticsPage.tsx. KPI strip + multi-series timeline chart
// (custom SVG, hover tooltip) + visitors table + per-visitor drawer.
// Skips Top Subjects / Top Findings (N/A for GutterInventory).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getAnalyticsSummary,
  getAnalyticsTimeline,
  getAnalyticsVisitors,
  getAnalyticsFailedLogins,
  getAnalyticsEventLog,
  type AnalyticsSummary,
  type AnalyticsTimelinePoint,
  type AnalyticsVisitor,
  type FailedLoginEvent,
  type AnalyticsEvent,
} from '../../api/admin.js';
import { VisitorDrawer } from '../../components/admin/VisitorDrawer.js';

type WindowDays = 1 | 7 | 30 | 90;

export function AnalyticsPage() {
  const [days, setDays] = useState<WindowDays>(1);
  const [openVisitor, setOpenVisitor] = useState<AnalyticsVisitor | null>(null);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [userEmailFilter, setUserEmailFilter] = useState<string>('all');

  const summaryQuery = useQuery({
    queryKey: ['admin', 'analytics', 'summary', days],
    queryFn: () => getAnalyticsSummary(days),
  });
  const timelineQuery = useQuery({
    queryKey: ['admin', 'analytics', 'timeline', days],
    queryFn: () => getAnalyticsTimeline(days),
  });
  const visitorsQuery = useQuery({
    queryKey: ['admin', 'analytics', 'visitors', days],
    queryFn: () => getAnalyticsVisitors(days, 25),
  });
  const failedLoginsQuery = useQuery({
    queryKey: ['admin', 'analytics', 'failed-logins', days],
    queryFn: () => getAnalyticsFailedLogins(days, 25),
  });
  const eventLogQuery = useQuery({
    queryKey: ['admin', 'analytics', 'event-log', days, eventTypeFilter, userEmailFilter],
    queryFn: () =>
      getAnalyticsEventLog(days, {
        limit: 100,
        eventType: eventTypeFilter,
        userEmail: userEmailFilter,
      }),
  });

  const summary = summaryQuery.data ?? null;
  const timeline = timelineQuery.data?.timeline ?? [];
  const visitors = visitorsQuery.data?.visitors ?? [];
  const failedLogins = failedLoginsQuery.data?.events ?? [];
  const events = eventLogQuery.data?.events ?? [];
  const loading =
    summaryQuery.isLoading ||
    timelineQuery.isLoading ||
    visitorsQuery.isLoading ||
    failedLoginsQuery.isLoading;
  const eventsLoading = eventLogQuery.isLoading;
  const err =
    summaryQuery.error ??
    timelineQuery.error ??
    visitorsQuery.error ??
    failedLoginsQuery.error ??
    eventLogQuery.error;

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <header className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-2xl text-[rgb(var(--color-navy))]">Analytics</h1>
          <p className="text-sm text-[rgb(var(--color-navy)/0.6)] mt-1">
            Dashboard activity, login traffic, and bot hits across all users.
          </p>
        </div>
        <WindowSwitcher value={days} onChange={setDays} />
      </header>

      {err && (
        <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-severity-critical)/0.3)] bg-[rgb(var(--color-severity-critical)/0.05)] px-4 py-3 text-sm text-[rgb(var(--color-severity-critical))] mb-6">
          {err instanceof Error ? err.message : String(err)}
        </div>
      )}

      <KpiStrip summary={summary} loading={loading} />

      <section className="mt-8">
        <SectionHeading title="Daily activity" subtitle={`Last ${days} ${days === 1 ? 'day' : 'days'}`} />
        <TimelineChart points={timeline} loading={loading} />
      </section>

      <section className="mt-8">
        <SectionHeading
          title="Recent failed logins"
          subtitle={
            failedLogins.length > 0
              ? `${failedLogins.length} most-recent`
              : 'None in this window'
          }
        />
        <FailedLoginsTable rows={failedLogins} loading={loading} />
      </section>

      <section className="mt-8">
        <SectionHeading title="Visitors" subtitle="Distinct IPs (hashed) + authed users" />
        <VisitorsTable rows={visitors} loading={loading} onSelect={setOpenVisitor} />
      </section>

      <section className="mt-8">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <SectionHeading title="Events" subtitle="Recent individual events (most recent 100, filtered)" />
          <EventsFilters
            eventType={eventTypeFilter}
            onEventTypeChange={setEventTypeFilter}
            userEmail={userEmailFilter}
            onUserEmailChange={setUserEmailFilter}
            visitors={visitors}
          />
        </div>
        <EventsTable rows={events} loading={eventsLoading} />
      </section>

      <VisitorDrawer visitor={openVisitor} onClose={() => setOpenVisitor(null)} />
    </div>
  );
}

function WindowSwitcher({ value, onChange }: { value: WindowDays; onChange: (v: WindowDays) => void }) {
  const opts: { v: WindowDays; label: string }[] = [
    { v: 1, label: '24h' },
    { v: 7, label: '7d' },
    { v: 30, label: '30d' },
    { v: 90, label: '90d' },
  ];
  return (
    <div className="inline-flex rounded-[var(--radius-md)] bg-white/60 border border-[rgb(var(--color-navy)/0.15)] p-0.5">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-[var(--radius-sm)] transition-colors ${
            o.v === value
              ? 'bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))]'
              : 'text-[rgb(var(--color-navy)/0.7)] hover:text-[rgb(var(--color-navy))] hover:bg-[rgb(var(--color-navy)/0.05)]'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="font-display text-lg text-[rgb(var(--color-navy))]">{title}</h2>
      {subtitle && (
        <span className="text-xs text-[rgb(var(--color-navy)/0.5)] uppercase tracking-wider font-semibold">
          {subtitle}
        </span>
      )}
    </div>
  );
}

function KpiStrip({ summary, loading }: { summary: AnalyticsSummary | null; loading: boolean }) {
  const kpis = [
    { label: 'Page views', value: summary?.pageViews ?? 0, accent: 'navy' as const },
    { label: 'Unique visitors', value: summary?.uniqueVisitors ?? 0, accent: 'navy' as const },
    { label: 'Authed users', value: summary?.authedUsers ?? 0, accent: 'navy' as const },
    { label: 'Logins', value: summary?.totalLogins ?? 0, accent: 'navy' as const },
    {
      label: 'Failed logins',
      value: summary?.failedLogins ?? 0,
      accent: (summary?.failedLogins ?? 0) > 0 ? ('danger' as const) : ('navy' as const),
    },
    {
      label: 'Bot hits',
      value: summary?.botHits ?? 0,
      accent: (summary?.botHits ?? 0) > 0 ? ('warn' as const) : ('navy' as const),
    },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {kpis.map((k) => (
        <KpiCard key={k.label} {...k} loading={loading} />
      ))}
    </div>
  );
}

function KpiCard({
  label,
  value,
  accent,
  loading,
}: {
  label: string;
  value: number | string;
  accent: 'navy' | 'bronze' | 'warn' | 'danger';
  loading: boolean;
}) {
  const accentClasses = {
    navy: 'text-[rgb(var(--color-navy))]',
    bronze: 'text-[rgb(var(--color-bronze))]',
    warn: 'text-amber-600',
    danger: 'text-[rgb(var(--color-severity-critical))]',
  }[accent];
  return (
    <div className="bg-white/55 border border-[rgb(var(--color-navy)/0.15)] rounded-[var(--radius-md)] p-3 transition-colors hover:bg-white">
      <div className="text-[11px] uppercase tracking-widest text-[rgb(var(--color-navy)/0.55)] font-semibold">
        {label}
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${accentClasses}`}>
        {loading ? (
          <span className="inline-block w-12 h-6 rounded bg-[rgb(var(--color-navy)/0.08)] animate-pulse" />
        ) : (
          formatNumber(value)
        )}
      </div>
    </div>
  );
}

function TimelineChart({ points, loading }: { points: AnalyticsTimelinePoint[]; loading: boolean }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const layout = useMemo(() => {
    if (points.length === 0) return null;
    const max = Math.max(
      1,
      ...points.flatMap((p) => [p.page_views, p.logins, p.bot_hits]),
    );
    const w = 800;
    const h = 160;
    const paddingLeft = 40;
    const paddingRight = 8;
    const paddingTop = 8;
    const paddingBottom = 24;
    const innerW = w - paddingLeft - paddingRight;
    const innerH = h - paddingTop - paddingBottom;
    const stepX = points.length > 1 ? innerW / (points.length - 1) : 0;

    const xs = points.map((_, i) => paddingLeft + i * stepX);
    const ys = {
      page_views: points.map((p) => paddingTop + innerH - (p.page_views / max) * innerH),
      logins: points.map((p) => paddingTop + innerH - (p.logins / max) * innerH),
      bot_hits: points.map((p) => paddingTop + innerH - (p.bot_hits / max) * innerH),
    } as const;

    const linePath = (yArr: number[]) =>
      yArr.map((y, i) => `${i === 0 ? 'M' : 'L'}${xs[i].toFixed(1)},${y.toFixed(1)}`).join(' ');

    const areaPath = (yArr: number[]) => {
      const line = linePath(yArr);
      const lastX = xs[xs.length - 1] ?? paddingLeft;
      const baseline = paddingTop + innerH;
      return `${line} L${lastX.toFixed(1)},${baseline.toFixed(1)} L${paddingLeft.toFixed(1)},${baseline.toFixed(1)} Z`;
    };

    return {
      max, w, h, paddingLeft, paddingRight, paddingTop, paddingBottom, innerW, innerH, stepX, xs, ys,
      paths: {
        page_views_line: linePath(ys.page_views),
        page_views_area: areaPath(ys.page_views),
        logins: linePath(ys.logins),
        bot_hits: linePath(ys.bot_hits),
      },
      viewBox: `0 0 ${w} ${h}`,
    };
  }, [points]);

  useEffect(() => {
    setHoverIdx(null);
  }, [points]);

  if (loading) {
    return (
      <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/55 p-6 h-48 animate-pulse" />
    );
  }
  if (!layout || layout.max === 0) {
    return (
      <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/55 p-6 text-sm text-[rgb(var(--color-navy)/0.55)] text-center">
        No events recorded in this window yet. Once people use the dashboard, daily activity will populate here.
      </div>
    );
  }

  const lastDay = points[points.length - 1]?.day ?? null;

  function svgPointFromMouse(e: React.MouseEvent<SVGSVGElement>): { x: number } | null {
    const svg = svgRef.current;
    if (!svg || !layout) return null;
    const rect = svg.getBoundingClientRect();
    const xRatio = (e.clientX - rect.left) / rect.width;
    return { x: xRatio * layout.w };
  }

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const pt = svgPointFromMouse(e);
    if (!pt || !layout) return;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < layout.xs.length; i++) {
      const d = Math.abs(layout.xs[i] - pt.x);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    setHoverIdx(best);
  }

  const active = hoverIdx != null ? points[hoverIdx] : null;
  const activeX = hoverIdx != null ? layout.xs[hoverIdx] : null;
  const tooltipLeftPct =
    activeX != null ? Math.min(Math.max((activeX / layout.w) * 100, 8), 92) : 0;

  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/70 p-4 relative">
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={layout.viewBox}
          className="w-full h-48 select-none"
          preserveAspectRatio="none"
          role="img"
          aria-label="Daily activity timeline"
          onMouseMove={handleMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          <defs>
            <linearGradient id="pv-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(27, 42, 63)" stopOpacity="0.18" />
              <stop offset="100%" stopColor="rgb(27, 42, 63)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 0.5, 1].map((frac) => {
            const y = layout.paddingTop + layout.innerH * (1 - frac);
            const value = Math.round(layout.max * frac);
            return (
              <g key={frac}>
                <line
                  x1={layout.paddingLeft}
                  x2={layout.w - 8}
                  y1={y}
                  y2={y}
                  stroke="rgb(27,42,63)"
                  strokeOpacity="0.07"
                />
                <text
                  x={layout.paddingLeft - 6}
                  y={y + 3}
                  fontSize="9"
                  fill="rgb(27,42,63)"
                  fillOpacity="0.45"
                  textAnchor="end"
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  {value}
                </text>
              </g>
            );
          })}

          {activeX != null && (
            <line
              x1={activeX}
              x2={activeX}
              y1={layout.paddingTop}
              y2={layout.paddingTop + layout.innerH}
              stroke="rgb(27,42,63)"
              strokeOpacity="0.35"
              strokeWidth="1"
              strokeDasharray="2 2"
            />
          )}

          <path d={layout.paths.page_views_area} fill="url(#pv-fill)" />
          <path
            d={layout.paths.page_views_line}
            fill="none"
            stroke="rgb(27,42,63)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={layout.paths.logins}
            fill="none"
            stroke="rgb(34, 110, 88)"
            strokeWidth="1.4"
            strokeDasharray="3 3"
            strokeLinecap="round"
          />
          <path
            d={layout.paths.bot_hits}
            fill="none"
            stroke="rgb(217, 119, 6)"
            strokeWidth="1.2"
            strokeDasharray="2 4"
            strokeLinecap="round"
          />

          {hoverIdx != null &&
            (
              [
                { y: layout.ys.page_views[hoverIdx], color: 'rgb(27,42,63)' },
                { y: layout.ys.logins[hoverIdx], color: 'rgb(34,110,88)' },
                { y: layout.ys.bot_hits[hoverIdx], color: 'rgb(217,119,6)' },
              ] as const
            ).map((d, i) => (
              <circle
                key={i}
                cx={layout.xs[hoverIdx]}
                cy={d.y}
                r="3"
                fill="white"
                stroke={d.color}
                strokeWidth="2"
              />
            ))}
        </svg>

        {active && (
          <div
            className="pointer-events-none absolute -top-2 -translate-x-1/2 -translate-y-full bg-[rgb(var(--color-navy))] text-[rgb(var(--color-linen))] rounded-[var(--radius-md)] px-3 py-2 text-[11px] shadow-lg min-w-[160px]"
            style={{ left: `${tooltipLeftPct}%` }}
          >
            <div className="font-semibold mb-1.5 text-[12px] tracking-wide">
              {formatTooltipDate(active.day)}
            </div>
            <TooltipRow color="rgb(255,255,255)" label="Page views" value={active.page_views} />
            <TooltipRow color="rgb(115,200,150)" label="Logins" value={active.logins} />
            <TooltipRow color="rgb(255,180,90)" label="Bot hits" value={active.bot_hits} />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[rgb(var(--color-navy)/0.65)] mt-2 px-1">
        <Legend dot="rgb(27,42,63)" label="Page views" />
        <Legend dot="rgb(34,110,88)" label="Logins" dashed />
        <Legend dot="rgb(217,119,6)" label="Bot hits" dashed />
        {lastDay && (
          <span className="ml-auto text-[rgb(var(--color-navy)/0.45)]">Through {formatDate(lastDay)}</span>
        )}
      </div>
    </div>
  );
}

function TooltipRow({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <div className="flex items-center gap-2 leading-relaxed">
      <span
        className="inline-block w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-[rgb(var(--color-linen)/0.75)]">{label}</span>
      <span className="font-mono tabular-nums">{value.toLocaleString()}</span>
    </div>
  );
}

function Legend({ dot, label, dashed }: { dot: string; label: string; dashed?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-[2px] rounded"
        style={{
          backgroundColor: dashed ? 'transparent' : dot,
          ...(dashed && {
            backgroundImage: `linear-gradient(90deg, ${dot} 50%, transparent 50%)`,
            backgroundSize: '4px 100%',
          }),
        }}
      />
      <span>{label}</span>
    </span>
  );
}

function VisitorsTable({
  rows,
  loading,
  onSelect,
}: {
  rows: AnalyticsVisitor[];
  loading: boolean;
  onSelect: (v: AnalyticsVisitor) => void;
}) {
  if (loading) return <SkeletonTable rows={5} />;
  if (rows.length === 0) {
    return <EmptyTable label="No visitors recorded in this window." />;
  }
  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/70 overflow-hidden">
      <table className="w-full text-sm table-auto">
        <thead className="bg-[rgb(var(--color-navy)/0.05)] text-[11px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.65)]">
          <tr>
            <th className="text-left font-semibold px-3 py-2">Visitor</th>
            <th className="text-left font-semibold px-3 py-2">Country</th>
            <th className="text-left font-semibold px-3 py-2 hidden md:table-cell">User-Agent</th>
            <th className="text-right font-semibold px-3 py-2 tabular-nums">Events</th>
            <th className="text-right font-semibold px-3 py-2 tabular-nums">Views</th>
            <th className="text-right font-semibold px-3 py-2 hidden lg:table-cell">Last seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ip_hash}
              className="border-t border-[rgb(var(--color-navy)/0.08)] hover:bg-[rgb(var(--color-navy)/0.04)] align-top cursor-pointer"
              onClick={() => onSelect(r)}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelect(r);
                }
              }}
            >
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.7)]">{r.ip_hash.slice(0, 16)}…</span>
                  {r.is_bot && (
                    <span className="px-1.5 py-0.5 rounded-[var(--radius-sm)] bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
                      Bot
                    </span>
                  )}
                </div>
                {r.user_email && (
                  <div
                    className="text-[11px] text-[rgb(var(--color-navy)/0.55)] break-all"
                    title={r.user_email}
                  >
                    {r.user_email}
                  </div>
                )}
              </td>
              <td className="px-3 py-2">
                <CountryCell code={r.country} />
              </td>
              <td className="px-3 py-2 hidden md:table-cell text-[11px] text-[rgb(var(--color-navy)/0.55)] max-w-[36ch] truncate" title={r.user_agent ?? ''}>
                {r.user_agent ?? '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-mono text-[rgb(var(--color-navy))]">
                {formatNumber(r.events)}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-mono text-[rgb(var(--color-navy)/0.7)]">
                {formatNumber(r.page_views)}
              </td>
              <td className="px-3 py-2 text-right hidden lg:table-cell text-[11px] text-[rgb(var(--color-navy)/0.55)]">
                {formatRelative(r.last_seen)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const REASON_LABEL: Record<string, string> = {
  email_not_authorized: 'Email not in app_users',
  no_session: 'Token verify returned no session',
  'Token has expired or is invalid': 'Token expired / invalid',
};

function prettyReason(reason: string | null): string {
  if (!reason) return '—';
  return REASON_LABEL[reason] ?? reason;
}

function EventsFilters({
  eventType,
  onEventTypeChange,
  userEmail,
  onUserEmailChange,
  visitors,
}: {
  eventType: string;
  onEventTypeChange: (v: string) => void;
  userEmail: string;
  onUserEmailChange: (v: string) => void;
  visitors: AnalyticsVisitor[];
}) {
  // User dropdown options come from visitors in the current window —
  // only people who've actually been seen show up. Deduped by email.
  // Filtering is by email (GutterInventory tracks user_email directly;
  // no user_id resolution required).
  const userOptions = (() => {
    const seen = new Set<string>();
    const opts: string[] = [];
    for (const v of visitors) {
      if (v.user_email && !seen.has(v.user_email)) {
        seen.add(v.user_email);
        opts.push(v.user_email);
      }
    }
    opts.sort((a, b) => a.localeCompare(b));
    return opts;
  })();

  const selectCls =
    'rounded-[var(--radius-sm)] bg-white border border-[rgb(var(--color-navy)/0.2)] px-2.5 py-1.5 text-xs font-medium text-[rgb(var(--color-navy))] ' +
    'focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-bronze)/0.4)] max-w-[28ch]';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <label className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.55)] font-semibold">
          Type
        </span>
        <select
          value={eventType}
          onChange={(e) => onEventTypeChange(e.target.value)}
          className={selectCls}
        >
          <option value="all">All</option>
          <option value="pageview">Page view</option>
          <option value="login_send">Login send</option>
          <option value="login_success">Login success</option>
          <option value="login_fail">Login fail</option>
        </select>
      </label>
      <label className="flex items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.55)] font-semibold">
          User
        </span>
        <select
          value={userEmail}
          onChange={(e) => onUserEmailChange(e.target.value)}
          className={selectCls}
        >
          <option value="all">All</option>
          {userOptions.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function EventsTable({
  rows,
  loading,
}: {
  rows: AnalyticsEvent[];
  loading: boolean;
}) {
  if (loading) return <SkeletonTable rows={6} />;
  if (rows.length === 0) {
    return <EmptyTable label="No events match the current filters in this window." />;
  }
  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/70 overflow-hidden">
      <table className="w-full text-sm table-auto">
        <thead className="bg-[rgb(var(--color-navy)/0.05)] text-[11px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.65)]">
          <tr>
            <th className="text-left font-semibold px-3 py-2">Time</th>
            <th className="text-left font-semibold px-3 py-2">Event</th>
            <th className="text-left font-semibold px-3 py-2">User</th>
            <th className="text-left font-semibold px-3 py-2 hidden md:table-cell">Detail</th>
            <th className="text-left font-semibold px-3 py-2 hidden lg:table-cell">Country</th>
            <th className="text-left font-semibold px-3 py-2 hidden lg:table-cell">IP</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.createdAt}-${i}`}
              className="border-t border-[rgb(var(--color-navy)/0.08)] align-top hover:bg-[rgb(var(--color-navy)/0.04)]"
            >
              <td className="px-3 py-2 text-[11px] text-[rgb(var(--color-navy)/0.7)] whitespace-nowrap">
                {new Date(r.createdAt).toLocaleString(undefined, { hour12: false })}
              </td>
              <td className="px-3 py-2">
                <EventTypeBadge type={r.eventType} isBot={r.isBot} />
              </td>
              <td className="px-3 py-2 text-[12px] text-[rgb(var(--color-navy))]">
                {r.userEmail ? (
                  <span title={r.userEmail}>{r.userEmail}</span>
                ) : (
                  <span className="text-[rgb(var(--color-navy)/0.45)] italic">Anonymous</span>
                )}
              </td>
              <td className="px-3 py-2 hidden md:table-cell text-[11px] text-[rgb(var(--color-navy)/0.65)] max-w-[40ch]">
                <EventDetail event={r} />
              </td>
              <td className="px-3 py-2 hidden lg:table-cell">
                <CountryCell code={r.country} />
              </td>
              <td className="px-3 py-2 hidden lg:table-cell font-mono text-[10px] text-[rgb(var(--color-navy)/0.55)]">
                {r.ipHash ? `${r.ipHash.slice(0, 16)}…` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventTypeBadge({ type, isBot }: { type: string; isBot: boolean }) {
  const cls =
    {
      pageview: 'bg-sky-100 text-sky-800',
      login_send: 'bg-indigo-100 text-indigo-800',
      login_success: 'bg-emerald-100 text-emerald-800',
      login_fail: 'bg-red-100 text-red-800',
    }[type] ?? 'bg-slate-100 text-slate-700';
  return (
    <div className="flex items-center gap-1.5">
      <span
        className={`px-1.5 py-0.5 rounded-[var(--radius-sm)] text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${cls}`}
      >
        {type.replace(/_/g, ' ')}
      </span>
      {isBot && (
        <span className="px-1.5 py-0.5 rounded-[var(--radius-sm)] bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
          Bot
        </span>
      )}
    </div>
  );
}

function EventDetail({ event }: { event: AnalyticsEvent }) {
  if (event.eventType === 'pageview') {
    return event.path ? (
      <span className="font-mono truncate" title={event.path}>
        {event.path}
      </span>
    ) : (
      <span className="text-[rgb(var(--color-navy)/0.45)]">—</span>
    );
  }
  if (event.eventType === 'login_fail') {
    const reason = typeof event.detail?.reason === 'string' ? event.detail.reason : null;
    return (
      <span className="text-[11px] text-[rgb(var(--color-severity-critical))]">
        {prettyReason(reason)}
      </span>
    );
  }
  if (event.eventType === 'login_success' || event.eventType === 'login_send') {
    return <span className="text-[11px] text-[rgb(var(--color-navy)/0.45)]">—</span>;
  }
  return <span className="text-[11px] text-[rgb(var(--color-navy)/0.45)]">—</span>;
}

function FailedLoginsTable({
  rows,
  loading,
}: {
  rows: FailedLoginEvent[];
  loading: boolean;
}) {
  if (loading) return <SkeletonTable rows={3} />;
  if (rows.length === 0) {
    return (
      <EmptyTable label="No failed login attempts in this window — clean slate." />
    );
  }
  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/70 overflow-hidden">
      <table className="w-full text-sm table-auto">
        <thead className="bg-[rgb(var(--color-navy)/0.05)] text-[11px] uppercase tracking-wider text-[rgb(var(--color-navy)/0.65)]">
          <tr>
            <th className="text-left font-semibold px-3 py-2">When</th>
            <th className="text-left font-semibold px-3 py-2">Email attempted</th>
            <th className="text-left font-semibold px-3 py-2">Reason</th>
            <th className="text-left font-semibold px-3 py-2">Country</th>
            <th className="text-left font-semibold px-3 py-2 hidden md:table-cell">
              User-Agent
            </th>
            <th className="text-left font-semibold px-3 py-2 hidden lg:table-cell">
              IP (hashed)
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-t border-[rgb(var(--color-navy)/0.08)] align-top hover:bg-[rgb(var(--color-navy)/0.04)]"
            >
              <td className="px-3 py-2 text-[11px] text-[rgb(var(--color-navy)/0.7)] whitespace-nowrap">
                {new Date(r.createdAt).toLocaleString(undefined, { hour12: false })}
              </td>
              <td className="px-3 py-2 text-[12px] text-[rgb(var(--color-navy))] break-all">
                {r.email ?? (
                  <span className="text-[rgb(var(--color-navy)/0.4)]">
                    (no email — bad token)
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-[12px] text-[rgb(var(--color-severity-critical))]">
                {prettyReason(r.reason)}
              </td>
              <td className="px-3 py-2">
                <CountryCell code={r.country} />
              </td>
              <td
                className="px-3 py-2 hidden md:table-cell text-[11px] text-[rgb(var(--color-navy)/0.55)] max-w-[36ch] truncate"
                title={r.userAgent ?? ''}
              >
                {r.userAgent ?? '—'}
              </td>
              <td className="px-3 py-2 hidden lg:table-cell font-mono text-[10px] text-[rgb(var(--color-navy)/0.55)]">
                {r.ipHash ? `${r.ipHash.slice(0, 16)}…` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountryCell({ code }: { code: string | null }) {
  if (!code || code === 'XX') {
    return <span className="text-[11px] text-[rgb(var(--color-navy)/0.4)]">—</span>;
  }
  if (code === 'T1') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px]">
        <span aria-label="Tor">🧅</span>
        <span className="font-mono text-[rgb(var(--color-navy)/0.65)]">Tor</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden="true">{flagEmoji(code)}</span>
      <span className="font-mono text-[11px] text-[rgb(var(--color-navy)/0.65)]">{code}</span>
    </span>
  );
}

function flagEmoji(iso2: string): string {
  if (!/^[A-Z]{2}$/.test(iso2)) return '🏳️';
  const A = 0x1f1e6;
  return String.fromCodePoint(A + iso2.charCodeAt(0) - 65, A + iso2.charCodeAt(1) - 65);
}

function SkeletonTable({ rows }: { rows: number }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/55 p-4 space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-7 rounded bg-[rgb(var(--color-navy)/0.05)] animate-pulse" />
      ))}
    </div>
  );
}

function EmptyTable({ label }: { label: string }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-[rgb(var(--color-navy)/0.15)] bg-white/55 p-6 text-sm text-[rgb(var(--color-navy)/0.55)] text-center">
      {label}
    </div>
  );
}

function formatNumber(v: number | string): string {
  if (typeof v === 'string') return v;
  if (v >= 10000) return `${(v / 1000).toFixed(1)}k`;
  return v.toLocaleString();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatTooltipDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
}
